import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import axios from "axios";
import { configureDisplayPreferences } from "@/utils/datetime";
import { addNativeAppListener, clearNativeSessionCookies, stripSessionTokens } from "@/native/runtime";

const AuthContext = createContext(null);
const AUTH_API = `${process.env.REACT_APP_BACKEND_URL}/api/auth`;
const IDLE_TIMEOUT_MS = 15 * 60 * 1000;
const REFRESH_COOLDOWN_MS = 60 * 1000;
const REFRESH_LOCK_KEY = "jfm-auth-refresh-lock";
const REFRESH_LAST_KEY = "jfm-auth-last-refresh";
const REFRESH_LOCK_TTL_MS = 15 * 1000;

function refreshLockId() {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

axios.defaults.withCredentials = true;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const setSessionUser = useCallback((next) => {
    const sessionUser = stripSessionTokens(next);
    configureDisplayPreferences(sessionUser || {});
    setUser(sessionUser);
  }, []);
  const [loading, setLoading] = useState(true);
  const idleTimerRef = useRef(null);
  const lastRefreshRef = useRef(Date.now());
  const refreshRequestRef = useRef(null);
  const featureRefreshRef = useRef(null);
  const logoutRequestRef = useRef(null);
  const sessionEpochRef = useRef(0);
  const lastActivityRef = useRef(Date.now());
  const activityScopeRef = useRef(null);
  const refreshLockIdRef = useRef(refreshLockId());

  const loadSession = useCallback(async ({ preserveOnError = false } = {}) => {
    if (logoutRequestRef.current) return;
    const epoch = sessionEpochRef.current;
    try {
      const response = await axios.get(`${AUTH_API}/me`);
      if (!logoutRequestRef.current && epoch === sessionEpochRef.current) setSessionUser(response.data);
    } catch (error) {
      if (epoch === sessionEpochRef.current && (!preserveOnError || error.response?.status === 401)) setSessionUser(null);
    } finally {
      setLoading(false);
    }
  }, [setSessionUser]);

  useEffect(() => {
    const responses = axios.interceptors?.response;
    if (!responses) return undefined;
    const id = responses.use(undefined, async (error) => {
      if ([403, 422].includes(error.response?.status) && typeof Blob !== 'undefined' && error.response?.data instanceof Blob) {
        try { error.response.data = JSON.parse(await error.response.data.text()); } catch { /* Keep the caller's existing delivery-error handling. */ }
      }
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : detail?.errors?.map((row) => row.message).join(' ') || '';
      const featureDisabled = [403, 422].includes(error.response?.status) && /module is disabled for your company/i.test(message);
      if (featureDisabled && !String(error.config?.url || '').startsWith(AUTH_API) && !featureRefreshRef.current) {
        featureRefreshRef.current = loadSession({ preserveOnError: true }).finally(() => { featureRefreshRef.current = null; });
      }
      // Never replay a failed mutation. Its caller keeps the readable denial.
      return Promise.reject(error);
    });
    return () => responses.eject(id);
  }, [loadSession]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const login = async (email, password) => {
    // Do not let a preceding logout delete a newly issued native cookie.
    try { await logoutRequestRef.current; } catch { /* a new login can replace an expired session */ }
    const epoch = ++sessionEpochRef.current;
    const response = await axios.post(`${AUTH_API}/login`, { email, password });
    const sessionUser = stripSessionTokens(response.data);
    if (epoch !== sessionEpochRef.current) return sessionUser;
    lastActivityRef.current = Date.now();
    lastRefreshRef.current = Date.now();
    flushSync(() => {
      setSessionUser(sessionUser);
    });
    return sessionUser;
  };

  const clearSession = useCallback(() => {
    sessionEpochRef.current += 1;
    flushSync(() => {
      setSessionUser(null);
    });
  }, [setSessionUser]);

  const logout = useCallback(async () => {
    if (logoutRequestRef.current) return logoutRequestRef.current;
    sessionEpochRef.current += 1;
    const request = (async () => {
      try {
        // Finish an already-started refresh before deleting its cookies. This
        // also lets the server revoke the refreshed session on explicit logout.
        try { await refreshRequestRef.current; } catch { /* still clear the session */ }
        await axios.post(`${AUTH_API}/logout`);
      } finally {
        try { await clearNativeSessionCookies(); } finally { clearSession(); }
      }
    })();
    logoutRequestRef.current = request;
    clearSession();
    try {
      return await request;
    } finally {
      if (logoutRequestRef.current === request) logoutRequestRef.current = null;
    }
  }, [clearSession]);

  const refreshActiveSession = useCallback(async () => {
    if (logoutRequestRef.current) return;
    const epoch = sessionEpochRef.current;
    const now = Date.now();
    const sharedLastRefresh = Number(
      window.localStorage.getItem(REFRESH_LAST_KEY) || "0",
    );
    if (
      now - Math.max(lastRefreshRef.current, sharedLastRefresh) <
      REFRESH_COOLDOWN_MS
    ) {
      return;
    }
    if (refreshRequestRef.current) {
      return refreshRequestRef.current;
    }

    const runRefresh = async () => {
      if (logoutRequestRef.current || epoch !== sessionEpochRef.current) return;
      const refreshedWhileWaiting = Number(
        window.localStorage.getItem(REFRESH_LAST_KEY) || "0",
      );
      if (Date.now() - refreshedWhileWaiting < REFRESH_COOLDOWN_MS) {
        lastRefreshRef.current = refreshedWhileWaiting;
        return;
      }
      try {
        const response = await axios.post(`${AUTH_API}/refresh`);
        if (epoch !== sessionEpochRef.current) return;
        const refreshedAt = Date.now();
        window.localStorage.setItem(REFRESH_LAST_KEY, String(refreshedAt));
        lastRefreshRef.current = refreshedAt;
        setSessionUser(response.data);
      } catch (error) {
        if (epoch === sessionEpochRef.current && [401, 403].includes(error.response?.status)) {
          setSessionUser(null);
        }
      }
    };

    const runWithLocalFallbackLock = async () => {
      const lockOwner = refreshLockIdRef.current;
      let existingLock = null;
      try {
        existingLock = JSON.parse(window.localStorage.getItem(REFRESH_LOCK_KEY) || "null");
      } catch {
        window.localStorage.removeItem(REFRESH_LOCK_KEY);
      }
      if (existingLock?.expiresAt > now && existingLock.owner !== lockOwner) {
        lastRefreshRef.current = now;
        return;
      }
      window.localStorage.setItem(
        REFRESH_LOCK_KEY,
        JSON.stringify({ owner: lockOwner, expiresAt: now + REFRESH_LOCK_TTL_MS }),
      );
      try {
        await runRefresh();
      } finally {
        const activeLock = JSON.parse(window.localStorage.getItem(REFRESH_LOCK_KEY) || "null");
        if (activeLock?.owner === lockOwner) {
          window.localStorage.removeItem(REFRESH_LOCK_KEY);
        }
      }
    };

    const refreshRequest = navigator.locks?.request
      ? navigator.locks.request(REFRESH_LOCK_KEY, { ifAvailable: true }, async (lock) => {
          if (!lock) {
            lastRefreshRef.current = Date.now();
            return;
          }
          await runRefresh();
        })
      : runWithLocalFallbackLock();
    refreshRequestRef.current = refreshRequest;
    try {
      return await refreshRequest;
    } finally {
      refreshRequestRef.current = null;
    }
  }, [setSessionUser]);

  const activityScope = user ? `${user.id}:${user.company_id}` : null;
  useEffect(() => {
    if (!activityScope) {
      window.clearTimeout(idleTimerRef.current);
      activityScopeRef.current = null;
      return undefined;
    }
    if (activityScopeRef.current !== activityScope) {
      activityScopeRef.current = activityScope;
      lastActivityRef.current = Date.now();
    }
    const signOutForInactivity = () => {
      // Timer/event handlers must not create an unhandled network rejection.
      clearSession();
      logout().catch(() => {});
    };
    const scheduleRemainingIdleTime = () => {
      window.clearTimeout(idleTimerRef.current);
      const remaining = IDLE_TIMEOUT_MS - (Date.now() - lastActivityRef.current);
      if (remaining <= 0) {
        signOutForInactivity();
        return false;
      }
      idleTimerRef.current = window.setTimeout(signOutForInactivity, remaining);
      return true;
    };
    const resetIdleTimer = () => {
      if (!scheduleRemainingIdleTime()) return;
      lastActivityRef.current = Date.now();
      scheduleRemainingIdleTime();
      refreshActiveSession();
    };
    const activityEvents = ["pointerdown", "keydown", "touchstart"];
    activityEvents.forEach((eventName) => window.addEventListener(eventName, resetIdleTimer));
    scheduleRemainingIdleTime();
    const removeAppStateListener = addNativeAppListener("appStateChange", ({ isActive }) => {
      // Returning from the OS share sheet/background is not user activity.
      // Suspended WebView timers cannot extend the existing fifteen-minute limit.
      if (isActive && scheduleRemainingIdleTime()) refreshActiveSession();
    });
    return () => {
      removeAppStateListener();
      window.clearTimeout(idleTimerRef.current);
      activityEvents.forEach((eventName) => window.removeEventListener(eventName, resetIdleTimer));
    };
  }, [clearSession, logout, refreshActiveSession, activityScope]);

  const updateDisplayPreferences = async (preferences) => {
    const epoch = sessionEpochRef.current;
    const response = await axios.patch(`${AUTH_API}/display-preferences`, preferences);
    const sessionUser = stripSessionTokens(response.data);
    if (epoch === sessionEpochRef.current) setSessionUser(sessionUser);
    return sessionUser;
  };

  const value = {
    user,
    loading,
    login,
    logout,
    clearSession,
    updateDisplayPreferences,
    refresh: loadSession,
    hasPermission: (permission) =>
      Boolean(user?.permissions?.includes(permission)),
    isFeatureEnabled: (feature) => {
      if (!user) return false;
      if (user.platform_role === "platform_owner") return true;
      const flag = user.features?.[feature];
      return flag === undefined ? true : Boolean(flag);
    },
    canAccessBilling: Boolean(user?.permissions?.includes("billing:read")),
    canManageProjectWorkflow: Boolean(
      user?.permissions?.includes("project:workflow"),
    ),
    canManageActiveJobs: Boolean(
      user?.permissions?.includes("active_jobs:manage"),
    ),
    canManageRecords: Boolean(user?.permissions?.includes("records:manage")),
    canManageCommunications: Boolean(
      user?.permissions?.includes("communications:manage"),
    ),
    canCreateFieldNotes: Boolean(
      user?.permissions?.includes("communications:manage") ||
        user?.permissions?.includes("field_notes:create"),
    ),
    canManageUsers: Boolean(user?.permissions?.includes("users:manage")),
    canManagePayroll: Boolean(user?.permissions?.includes("payroll:manage")),
    canAccessAnalytics: Boolean(user?.permissions?.includes("analytics:read")),
    canManageAssets: Boolean(user?.permissions?.includes("assets:manage")),
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}

